// @flow
import { List } from 'immutable';
import type { Record } from 'immutable';
import Catalog from './Catalog';
import {
  Review,
  Image,
  Video,
  Question,
  Answer,
  Response,
  WhyDidYouBuy,
} from '.';
import getArchetype from './getUgcArchetype';

const Ugcs = {
  createWithData(data: { ugc: UgcBlob }) {
    const {
      ugc,
      ugc: { ugctype, ugc_related_content, catalog },
    } = data;
    // assumption is that Ugcs will only be nested one level
    const ugc_archetype = getArchetype(ugctype);
    const arg = {
      ...ugc,
      ugc_archetype,
      catalog: Boolean(catalog) ? new Catalog(catalog) : Catalog.Empty,
      ugc_related_content: List(
        !ugc_related_content
          ? []
          : ugc_related_content.map(relatedUgc => {
              const relatedUgcWithDescendants = {
                ...relatedUgc,
                ugc_related_content: ugc_related_content.filter(
                  u => u.parent_ugc_id && u.parent_ugc_id === relatedUgc.ugc_id
                ),
              };
              return Ugcs.createWithData({
                ugc: relatedUgcWithDescendants,
              });
            })
      ),
    };

    switch (ugc_archetype) {
      case 'Review':
        return Review.createWithData(arg);

      case 'Image':
        return Image.createWithData(arg);

      case 'Video':
        return Video.createWithData(arg);

      case 'Question':
        return Question.createWithData(arg);

      case 'Answer':
        return Answer.createWithData(arg);

      case 'WDYB':
        return WhyDidYouBuy.createWithData({ ...ugc, ugc_archetype });

      case 'MerchantResponse':
        return Response.createWithData(arg);

      default: {
        throw new Error(`Unsupported ugctype: ${ugctype}.`);
      }
    }
  },

  getIdFromData(data: ContentBlob) {
    const { ugc: { ugc_id } = { ugc_id: null } } = data;

    if (!ugc_id) {
      throw new Error('ugc_id is undefined');
    }

    return ugc_id;
  },

  getId(record: Record<UgcBlob>) {
    return record.get('ugc_id');
  },

  getSubjectId(record: Record<UgcBlob>) {
    return record.get('subject_id');
  },

  getRelatedItemByType(record: Record<UgcBlob>, type: UgcType) {
    return record.get('ugc_related_content').find(ugc => ugc.ugctype === type);
  },

  getRelatedItemsByType(record: Record<UgcBlob>, type: UgcType) {
    return record
      .get('ugc_related_content')
      .filter(ugc => ugc.ugctype === type);
  },

  getMerchantResponse(record: Record<UgcBlob>) {
    return Ugcs.getRelatedItemByType(
      record,
      ('MerchantResponse': ResponseUgcType)
    );
  },

  getBrandResponse(record: Record<UgcBlob>) {
    return Ugcs.getRelatedItemByType(
      record,
      ('BrandMerchantResponse': ResponseUgcType)
    );
  },

  hasResponses(record: Record<UgcBlob>): boolean {
    return Ugcs.getBrandResponse(record) || Ugcs.getMerchantResponse(record);
  },

  hasBrandResponseOrAnswer(record: Record<UgcBlob>): boolean {
    return Boolean(
      record
        .get('ugc_related_content')
        .find(
          ({ ugctype }) =>
            ugctype === 'BrandAnswer' || ugctype === 'BrandMerchantResponse'
        )
    );
  },
};

export default Ugcs;



// WEBPACK FOOTER //
// ./src/models/Ugc.js